import {
  Refrigerator,
  Snowflake,
  Droplets,
  Zap,
  HouseWifi,
  Warehouse,
  LucideIcon,
} from 'lucide-react';
import { serviceCategories } from './services';

export interface ServiceNavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
}

const HOME_APPLIANCE_CATEGORY_IDS = new Set(['appliance-repair', 'appliance-installation']);

export const HOME_APPLIANCE_SERVICE_IDS = new Set(
  serviceCategories
    .filter((c) => HOME_APPLIANCE_CATEGORY_IDS.has(c.id))
    .flatMap((c) => c.services.map((s) => s.id)),
);

export const PLUMBING_SERVICE_IDS = new Set(
  serviceCategories
    .find((c) => c.id === 'plumbing-services')
    ?.services.map((s) => s.id) ?? [],
);

export const ELECTRICAL_SERVICE_IDS = new Set(
  serviceCategories
    .find((c) => c.id === 'electrical-services')
    ?.services.map((s) => s.id) ?? [],
);

/** Header Services dropdown — category list only (no individual repair services) */
export const serviceNavItems: ServiceNavItem[] = [
  { id: 'home-appliances', label: 'Home Appliances', path: '/services/home-appliances', icon: Refrigerator },
  { id: 'hvac-services', label: 'HVAC Services', path: '/services/hvac-support', icon: Snowflake },
  { id: 'plumbing-services', label: 'Plumbing Services', path: '/services/plumbing', icon: Droplets },
  { id: 'electrical-services', label: 'Electrical Services', path: '/services/electrical', icon: Zap },
  { id: 'smart-home-setup', label: 'Smart Home Setup', path: '/services/smart-thermostat-setup', icon: HouseWifi },
  { id: 'garage-door-repair', label: 'Garage Door Repair', path: '/services/garage-door-repair', icon: Warehouse },
];

export const serviceNavPath = (item: ServiceNavItem): string => item.path;

const SMART_HOME_PREFIXES = [
  '/services/smart-thermostat-setup',
  '/services/doorbell-installation',
  '/services/camera-installation',
  '/services/smart-lock-installation',
  '/services/wifi-setup',
  '/services/tv-mounting',
];

export const isServiceNavItemActive = (
  item: ServiceNavItem,
  pathname: string,
  search: string,
): boolean => {
  if (item.id === 'home-appliances') {
    if (pathname === '/services/home-appliances') return true;
    if (!pathname.startsWith('/services/')) return false;
    const segment = pathname.replace('/services/', '').split('/')[0];
    return HOME_APPLIANCE_SERVICE_IDS.has(segment);
  }

  if (item.id === 'plumbing-services') {
    if (pathname === '/services/plumbing' || pathname === '/services/plumbing-support') return true;
    if (!pathname.startsWith('/services/')) return false;
    const segment = pathname.replace('/services/', '').split('/')[0];
    return PLUMBING_SERVICE_IDS.has(segment);
  }

  if (item.id === 'electrical-services') {
    if (pathname === '/services/electrical' || pathname === '/services/electrical-support') return true;
    if (!pathname.startsWith('/services/')) return false;
    const segment = pathname.replace('/services/', '').split('/')[0];
    return ELECTRICAL_SERVICE_IDS.has(segment);
  }

  if (item.id === 'smart-home-setup') {
    return SMART_HOME_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
  }

  if (item.id === 'garage-door-repair') {
    return pathname === '/services/garage-door-repair';
  }

  return pathname === item.path;
};
